/**
 * bulkAction/BulkAssignModalWithScope.jsx
 *
 * Scoped KPI question assignment modal with Tabbed UI:
 *   1. Assign by Location (Location-first: Select locations -> filter users -> unique mapping)
 *   2. Assign by User (User-first: Select user -> select subset of their locations)
 *
 * Props:
 *   show                {boolean}
 *   onHide              {function}
 *   selectedIds         {number[]}  — checked KPI question IDs
 *   financialYearId     {number}
 *   locations           {array}     — [{id, name, location}] from useKpiData
 *   onSuccess           {function}  — called after successful save
 */
import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { S, FieldLabel, StyledSelect, Chip, Spin } from "./Shared";
import MultiSelectDropdown from "../../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import SearchableDropdown from "../../../../../globalComponents/form/dropdowns/SearchableDropdown";

const BulkAssignModalWithScope = ({
  show,
  onHide,
  selectedIds = [],
  financialYearId,
  locations = [],
  onSuccess,
}) => {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const [activeTab, setActiveTab] = useState("location"); // "location" or "user"
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Tab 1 (Location-First) state
  const [selectedLocationsScope, setSelectedLocationsScope] = useState([]); // [{id, name}]
  const [userPool, setUserPool] = useState([]); // list of filtered sub-users
  const [locationUserMap, setLocationUserMap] = useState({}); // { locationId: userId }
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Tab 2 (User-First) state
  const [allDataOwners, setAllDataOwners] = useState([]);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]); // [userId]
  const [ownerLocationMap, setOwnerLocationMap] = useState({}); // { userId: [locationId] }

  const [dbAssignments, setDbAssignments] = useState([]);
  const [loadingDbAssignments, setLoadingDbAssignments] = useState(false);

  const fetchDbAssignments = async () => {
    if (!selectedIds.length || !financialYearId) return;
    setLoadingDbAssignments(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/assigned-kpis`,
        {},
        {
          kpiIds: selectedIds.join(","),
          financialYearId,
        },
        "GET"
      );
      if (isSuccess && isMounted.current) {
        setDbAssignments(data?.data || []);
      }
    } catch (e) {
      console.error("Failed to fetch existing assignments", e);
    } finally {
      if (isMounted.current) setLoadingDbAssignments(false);
    }
  };

  const checkConflict = (locationId, userId) => {
    if (!userId) return false;

    // Check DB assignments
    for (const assignment of dbAssignments) {
      if (assignment.sourceIds?.map(Number).includes(Number(locationId))) {
        const hasDifferentUser = assignment.assignees?.some(
          (u) => u.userId && String(u.userId) !== String(userId)
        );
        if (hasDifferentUser) {
          return true;
        }
      }
    }

    // Check Tab 1 (locationUserMap)
    const tab1UserId = locationUserMap[locationId];
    if (tab1UserId && String(tab1UserId) !== String(userId)) {
      return true;
    }

    // Check Tab 2 (ownerLocationMap)
    for (const [oId, locIds] of Object.entries(ownerLocationMap)) {
      if ((locIds || []).map(Number).includes(Number(locationId))) {
        if (String(oId) !== String(userId)) {
          return true;
        }
      }
    }

    return false;
  };

  // Reset modal state on open
  useEffect(() => {
    if (!show) return;
    setError("");
    setActiveTab("location");
    setSelectedLocationsScope([]);
    setUserPool([]);
    setLocationUserMap({});
    setSelectedOwnerIds([]);
    setOwnerLocationMap({});
    setDbAssignments([]);
    fetchAllDataOwners();
    fetchDbAssignments();
  }, [show]);

  // Auto-select data owner in Tab 1 if there's only one eligible user for that location
  useEffect(() => {
    if (selectedLocationsScope.length === 0 || userPool.length === 0) return;
    let changed = false;
    const updatedMap = { ...locationUserMap };

    selectedLocationsScope.forEach((loc) => {
      const eligibleUsers = userPool.filter((u) => u.locations.includes(loc.id));
      if (eligibleUsers.length === 1) {
        const singleUserId = eligibleUsers[0].id;
        if (updatedMap[loc.id] !== singleUserId) {
          updatedMap[loc.id] = singleUserId;
          changed = true;
        }
      }
    });

    if (changed) {
      setLocationUserMap(updatedMap);
    }
  }, [selectedLocationsScope, userPool, locationUserMap]);

  // Auto-select location in Tab 2 if owner has exactly one location
  useEffect(() => {
    if (selectedOwnerIds.length === 0 || allDataOwners.length === 0) return;
    let changed = false;
    const updatedMap = { ...ownerLocationMap };

    selectedOwnerIds.forEach((uid) => {
      const owner = allDataOwners.find((o) => o.id === uid);
      if (owner && owner.locations.length === 1) {
        const singleLocId = owner.locations[0].id;
        if (!updatedMap[uid] || updatedMap[uid].length !== 1 || updatedMap[uid][0] !== singleLocId) {
          updatedMap[uid] = [singleLocId];
          changed = true;
        }
      }
    });

    if (changed) {
      setOwnerLocationMap(updatedMap);
    }
  }, [selectedOwnerIds, allDataOwners, ownerLocationMap]);

  // Fetch all data owners for Tab 2
  const fetchAllDataOwners = async () => {
    setLoadingUsers(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUser`,
        {},
        {},
        "GET"
      );
      if (isSuccess && isMounted.current) {
        // Filter users whose role from authorization module is "Data Owner"
        const filtered = (data?.data || []).filter(
          (u) => u.role?.toLowerCase() === "data owner"
        );

        const list = filtered.map((u) => {
          let parsedLocs = [];
          try {
            parsedLocs = (u.location || []).map((l) => {
              const matched = locations.find((loc) => Number(loc.id) === Number(l.sourceId));
              const resolvedName = matched?.location?.unitCode || matched?.unitCode || matched?.location?.name || matched?.name || l.location?.unitCode || l.location?.name || `Location ${l.sourceId}`;
              return {
                id: Number(l.sourceId),
                name: resolvedName,
              };
            });
          } catch (e) {
            parsedLocs = [];
          }
          return {
            id: String(u.userId),
            name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
              (u.designation ? ` (${u.designation})` : ""),
            locations: parsedLocs,
          };
        });
        setAllDataOwners(list);
      }
    } catch {
      setError("Failed to fetch sub-users. Please try again.");
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  // Fetch filtered users who have access to selected locations for Tab 1
  const fetchUsersForLocations = async (locIds) => {
    if (!locIds.length) {
      setUserPool([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUsersByLocations`,
        {},
        { locationIds: JSON.stringify(locIds) },
        "GET"
      );
      if (isSuccess && isMounted.current) {
        const incoming = (data?.data || []).map((u) => {
          let userLocIds = [];
          try {
            userLocIds = (u.location || []).map((l) => Number(l.sourceId));
          } catch (e) {
            userLocIds = [];
          }
          return {
            id: String(u.userId),
            name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
              (u.designation ? ` (${u.designation})` : ""),
            locations: userLocIds,
          };
        });
        setUserPool(incoming);
      }
    } catch {
      setError("Failed to filter users by location.");
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  // Handle Location selection in Tab 1
  const handleLocationAdd = (e) => {
    const rawVal = e.target.value;
    if (!rawVal) return;
    const id = Number(rawVal);
    e.target.value = "";
    if (selectedLocationsScope.some((l) => Number(l.id) === id)) return;

    const matchedLoc = locations.find((l) => Number(l.id) === id);
    const locName = matchedLoc?.location?.unitCode || matchedLoc?.unitCode || matchedLoc?.location?.name || matchedLoc?.name || `Location ${id}`;

    const updatedLocs = [...selectedLocationsScope, { id, name: locName }];
    setSelectedLocationsScope(updatedLocs);

    const locIds = updatedLocs.map((l) => l.id);
    fetchUsersForLocations(locIds);
  };

  const handleLocationRemove = (id) => {
    const updatedLocs = selectedLocationsScope.filter((l) => l.id !== id);
    setSelectedLocationsScope(updatedLocs);

    const updatedMap = { ...locationUserMap };
    delete updatedMap[id];
    setLocationUserMap(updatedMap);

    const locIds = updatedLocs.map((l) => l.id);
    fetchUsersForLocations(locIds);
  };

  // Handle location-user dropdown change in Tab 1
  const handleLocationUserChange = (locationId, userId) => {
    if (userId && checkConflict(locationId, userId)) {
      alert("You already selected the data owner for that particular location.");
      setError("You already selected the data owner for that particular location.");
      return;
    }
    setError("");
    setLocationUserMap((prev) => ({
      ...prev,
      [locationId]: userId,
    }));
  };



  const handleSave = async () => {
    setError("");
    let assignmentsPayload = [];

    if (activeTab === "location") {
      if (!selectedLocationsScope.length) {
        setError("Please select at least one location to assign scope.");
        return;
      }

      // Check if all selected locations have an assigned user
      const unassigned = selectedLocationsScope.filter((l) => !locationUserMap[l.id]);
      if (unassigned.length > 0) {
        setError(`Please assign a data owner for: ${unassigned.map((l) => l.name).join(", ")}`);
        return;
      }

      // Final validation for conflicts
      for (const [locId, uId] of Object.entries(locationUserMap)) {
        if (checkConflict(locId, uId)) {
          setError("You already selected the data owner for that particular location.");
          return;
        }
      }

      assignmentsPayload = Object.entries(locationUserMap).map(([locId, uId]) => ({
        locationId: Number(locId),
        userId: Number(uId),
      }));
    } else {
      if (!selectedOwnerIds.length) {
        setError("Please select at least one data owner.");
        return;
      }

      // Check if all selected owners have a location selected
      const unassignedOwners = selectedOwnerIds
        .map((uid) => allDataOwners.find((o) => o.id === uid))
        .filter((o) => o && (!ownerLocationMap[o.id] || ownerLocationMap[o.id].length === 0));

      if (unassignedOwners.length > 0) {
        setError(`Please select a location for: ${unassignedOwners.map((o) => o.name).join(", ")}`);
        return;
      }

      // Final validation for conflicts
      for (const [uId, locIds] of Object.entries(ownerLocationMap)) {
        for (const locId of locIds) {
          if (checkConflict(locId, uId)) {
            setError("You already selected the data owner for that particular location.");
            return;
          }
        }
      }

      assignmentsPayload = Object.entries(ownerLocationMap).flatMap(([uId, locIds]) =>
        (locIds || []).map((locId) => ({
          locationId: Number(locId),
          userId: Number(uId),
        }))
      );
    }

    setSaving(true);
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}assignQuestionScope`,
        {},
        {
          financialYearId,
          questionIds: selectedIds,
          moduleType: "SQ",
          questionnaireType: "CA",
          assignments: assignmentsPayload,
        },
        "POST"
      );

      if (isSuccess) {
        onSuccess?.();
        onHide();
      } else {
        setError("Scoped assignment failed. Please try again.");
      }
    } catch {
      setError("Server error. Please try again.");
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: 550, minHeight: 520 }}>
        {/* Header */}
        <div style={S.header}>
          <span style={S.title}>Assign Data Owner with Scope</span>
          <button onClick={onHide} disabled={saving} style={S.closeBtn}>✕</button>
        </div>

        {/* Tabs Bar */}
        <div
          style={{
            display: "flex",
            borderBottom: "2px solid #f1f5f9",
            margin: "12px 22px 0 22px",
          }}
        >
          <button
            onClick={() => setActiveTab("location")}
            disabled={saving}
            style={{
              flex: 1,
              padding: "10px 16px",
              fontSize: 13,
              fontWeight: 600,
              border: "none",
              background: "none",
              cursor: "pointer",
              transition: "all 0.2s",
              borderBottom: activeTab === "location" ? "2px solid #2563eb" : "2px solid transparent",
              color: activeTab === "location" ? "#2563eb" : "#64748b",
            }}
          >
            Assign by Location
          </button>
          <button
            onClick={() => setActiveTab("user")}
            disabled={saving}
            style={{
              flex: 1,
              padding: "10px 16px",
              fontSize: 13,
              fontWeight: 600,
              border: "none",
              background: "none",
              cursor: "pointer",
              transition: "all 0.2s",
              borderBottom: activeTab === "user" ? "2px solid #2563eb" : "2px solid transparent",
              color: activeTab === "user" ? "#2563eb" : "#64748b",
            }}
          >
            Assign by User
          </button>
        </div>

        {/* Body */}
        <div style={S.body}>
          {error && <div style={S.errBox}>{error}</div>}

          <div style={S.infoBox}>
            <b>{selectedIds.length}</b> KPI{selectedIds.length !== 1 ? "s" : ""} selected for scoped assignment.
          </div>

          {/* TAB 1: LOCATION FIRST */}
          {activeTab === "location" && (
            <div>
              <div style={S.field}>
                <FieldLabel>
                  Select Locations
                  {loadingUsers && <Spin />}
                </FieldLabel>
                <MultiSelectDropdown
                  options={locations.map((loc) => {
                    const locName = loc?.location?.unitCode || loc?.unitCode || loc?.location?.name || loc?.name || `Location ${loc.id}`;
                    return {
                      value: String(loc.id),
                      label: locName,
                    };
                  })}
                  selected={selectedLocationsScope.map((l) => String(l.id))}
                  onChange={(newLocIds) => {
                    const updatedLocs = newLocIds.map((idStr) => {
                      const matched = locations.find((l) => String(l.id) === idStr);
                      const locName = matched?.location?.unitCode || matched?.unitCode || matched?.location?.name || matched?.name || `Location ${idStr}`;
                      return { id: Number(idStr), name: locName };
                    });
                    setSelectedLocationsScope(updatedLocs);

                    // Fetch users for these locations
                    fetchUsersForLocations(updatedLocs.map((l) => l.id));

                    // Keep locationUserMap clean: remove keys that are no longer selected
                    setLocationUserMap((prev) => {
                      const cleanMap = { ...prev };
                      Object.keys(cleanMap).forEach((locId) => {
                        if (!newLocIds.includes(String(locId))) {
                          delete cleanMap[locId];
                        }
                      });
                      return cleanMap;
                    });
                  }}
                  placeholder="Select Locations..."
                  searchPlaceholder="Search locations..."
                  showSelectAll={true}
                />

              </div>

              {selectedLocationsScope.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.2fr",
                      padding: "8px 12px",
                      background: "#f8fafc",
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: 11,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 8,
                    }}
                  >
                    <span>Location</span>
                    <span>Assignee (Data Owner)</span>
                  </div>

                  <div style={{ maxHeight: 250, overflowY: "auto", paddingBottom: 150 }}>
                    {selectedLocationsScope.map((loc) => {
                      // Filter users who actually have access to this location
                      const eligibleUsers = userPool.filter((u) =>
                        u.locations.includes(loc.id)
                      );
                      const currentVal = locationUserMap[loc.id] || "";

                      return (
                        <div
                          key={loc.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1.2fr",
                            alignItems: "center",
                            padding: "8px 12px",
                            borderBottom: "1px solid #f1f5f9",
                            gap: 12,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
                            {loc.name}
                          </span>
                          <div style={{ position: "relative" }}>
                            <SearchableDropdown
                              options={eligibleUsers.map((u) => ({
                                value: u.id,
                                label: u.name,
                              }))}
                              value={currentVal}
                              onChange={(val) => handleLocationUserChange(loc.id, val)}
                              placeholder="— Select Data Owner —"
                              disabled={saving || loadingUsers}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: USER FIRST */}
          {activeTab === "user" && (
            <div>
              <div style={S.field}>
                <FieldLabel>
                  Select Data Owners
                  {loadingUsers && <Spin />}
                </FieldLabel>
                <MultiSelectDropdown
                  options={allDataOwners.map((owner) => ({
                    value: owner.id,
                    label: owner.name,
                  }))}
                  selected={selectedOwnerIds}
                  onChange={(newSelected) => {
                    setSelectedOwnerIds(newSelected);
                    setOwnerLocationMap((prev) => {
                      const cleanMap = { ...prev };
                      // Remove deselected owners
                      Object.keys(cleanMap).forEach((uid) => {
                        if (!newSelected.includes(uid)) {
                          delete cleanMap[uid];
                        }
                      });
                      // Auto-select single location
                      newSelected.forEach((uid) => {
                        const owner = allDataOwners.find((o) => o.id === uid);
                        if (owner && owner.locations.length === 1) {
                          const singleLocId = owner.locations[0].id;
                          if (!cleanMap[uid] || cleanMap[uid].length === 0) {
                            cleanMap[uid] = [singleLocId];
                          }
                        }
                      });
                      return cleanMap;
                    });
                  }}
                  placeholder="— Choose Data Owners —"
                  searchPlaceholder="Search data owners..."
                  showSelectAll={true}
                />
              </div>

              {selectedOwnerIds.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.2fr 1fr",
                      padding: "8px 12px",
                      background: "#f8fafc",
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: 11,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 8,
                    }}
                  >
                    <span>Assignee (Data Owner)</span>
                    <span>Location</span>
                  </div>

                  <div style={{ maxHeight: 250, overflowY: "auto", paddingBottom: 150 }}>
                    {selectedOwnerIds.map((ownerId) => {
                      const owner = allDataOwners.find((o) => o.id === ownerId);
                      if (!owner) return null;

                      // Filter user's locations to exclude any location already selected by ANOTHER user in ownerLocationMap.
                      const otherSelectedLocIds = Object.entries(ownerLocationMap)
                        .filter(([uid]) => uid !== ownerId)
                        .flatMap(([, locIds]) => (locIds || []).map(Number));

                      const availableLocs = owner.locations.filter(
                        (loc) => !otherSelectedLocIds.includes(Number(loc.id))
                      );

                      const currentSelected = ownerLocationMap[ownerId] || [];

                      return (
                        <div
                          key={ownerId}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1.2fr 1fr",
                            alignItems: "center",
                            padding: "8px 12px",
                            borderBottom: "1px solid #f1f5f9",
                            gap: 12,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
                            {owner.name}
                          </span>
                          <div style={{ position: "relative" }}>
                            {owner.locations.length === 0 ? (
                              <span style={{ fontSize: 12, color: "#ef4444" }}>
                                No locations configured
                              </span>
                            ) : (
                              <MultiSelectDropdown
                                options={availableLocs.map((loc) => ({
                                  value: loc.id,
                                  label: loc.name,
                                }))}
                                selected={currentSelected}
                                onChange={(newLocSelected) => {
                                  const added = newLocSelected.filter(locId => !currentSelected.includes(locId));
                                  let conflictFound = false;
                                  for (const locId of added) {
                                    if (checkConflict(locId, ownerId)) {
                                      alert("You already selected the data owner for that particular location.");
                                      setError("You already selected the data owner for that particular location.");
                                      conflictFound = true;
                                      break;
                                    }
                                  }
                                  if (conflictFound) {
                                    return;
                                  }
                                  setError("");
                                  setOwnerLocationMap((prev) => ({
                                    ...prev,
                                    [ownerId]: newLocSelected,
                                  }));
                                }}
                                placeholder="Select Locations..."
                                searchPlaceholder="Search locations..."
                                showSelectAll={true}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button onClick={onHide} disabled={saving} style={S.btnSecondary}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...S.btnPrimary, opacity: saving ? 0.75 : 1 }}
          >
            {saving ? (
              <>
                <Spin light size={12} /> Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModalWithScope;
